import {
  ActivityButton,
  Filter,
  type SelectItem,
  useSelectQueryGate,
} from '@vrplatform/components';
import {
  type gqlV2,
  useDashboard,
  useInfiniteQuery,
  useOwnerPortalContact,
  useQuery,
  useTeam,
} from '@vrplatform/data';
import { type Maybe, day, groupBy, hasValue } from '@vrplatform/utils';
import { Text } from '@mantine/core';
import { useDisclosure, useSetState } from '@mantine/hooks';
import {
  formatOwnerName,
  getListingName,
  whereListings,
} from '@vrplatform/ui-common';
import { useState } from 'react';
import {
  ACTIVITY_START_DATE,
  ActivityDeletedItemBadge,
  type ActivityDeltaJson,
  ActivityDrawer,
  type ActivityOperation,
  type ActivityPanelProps,
  type ActivityRow,
  type ActivityTable,
  getActivityActor,
  whereActorUserId,
} from '../../../drawers';
import { LISTING_ACTIVITY_TABLES, getListingActivityDetails } from './_queries';

export const ListingListActivityDrawer = () => {
  const [opened, handlers] = useDisclosure(false);

  return (
    <>
      <ActivityButton onClick={handlers.open} />
      <Drawer close={handlers.close} opened={opened} />
    </>
  );
};

type FilterState = {
  search: string;
  actorUserId: Maybe<string>;
  listingId: Maybe<string>;
  op: Maybe<ActivityOperation>;
};

const Drawer = ({ close, opened }: { opened: boolean; close: () => void }) => {
  const [{ id: teamId, isGL: GL }] = useTeam();
  const [dashboard] = useDashboard();
  const { contactId: ownerPortalContactId } = useOwnerPortalContact();

  const [filter, setFilter] = useSetState<FilterState>({
    actorUserId: null,
    search: '',
    listingId: null,
    op: null,
  });

  const queryData = useInfiniteQuery(
    (q, args, { limit, offset }) => {
      if (!args.teamId)
        return {
          aggregate: 0,
          list: [],
        };

      const where: gqlV2.audit_log_bool_exp = {
        tenantId: { _eq: args.teamId },
        tableName: { _in: args.LISTING_ACTIVITY_TABLES },
        createdAt: { _gte: args.ACTIVITY_START_DATE },
        op: args.op ? { _eq: args.op } : undefined,
        actorUserId: whereActorUserId(args.actorUserId),
        listingId: args.listingId
          ? { _eq: args.listingId }
          : { _is_null: false },
        listing: args.search
          ? whereListings({
              dashboard: args.dashboard,
              tenantId: args.teamId,
              search: args.search,
              ownerPortalContactId: args.ownerPortalContactId,
              GL: args.GL,
            })
          : undefined,
      };

      const aggregate = q.auditLogAggregate({ where }).aggregate?.count() || 0;

      const logs = q
        .auditLogs({
          where,
          order_by: [{ createdAt: 'desc' }],
          offset,
          limit,
        })
        .map((log) => {
          const getGroupRef = () => {
            if (log.tableName === 'listing_owner') {
              return `${log.ownerId}-update-${day(log.createdAt).yyyymmdd()}`;
            }

            return `${log.ownerId}-${log.op}-${day(log.createdAt).yyyymmdd()}`;
          };

          return {
            id: log.id,
            objectId: log.listingId!,
            op: log.op as ActivityOperation,
            tableName: log.tableName as ActivityTable,
            groupRef: getGroupRef(),
            actor: getActivityActor(log),
            date: log.createdAt,
            listingName: getListingName(log.listing),
            listingId: log.listingId!,
          };
        });

      const list = Object.entries(groupBy(logs, 'groupRef')).map<ActivityRow>(
        ([, logs]) => {
          const log = logs[0];

          const actions: Record<ActivityOperation, string> = {
            insert: 'Created',
            delete: 'Deleted',
            update: 'Updated',
          };

          let op = log.op;

          if (log.tableName !== 'listing') {
            op = 'update';
          }

          const getLabel = () => {
            return (
              <>
                <Text component="span" color="gray">
                  {actions[op]}
                </Text>{' '}
                {log.listingName ? (
                  <Text fw={500} component="span">
                    {log.listingName}
                  </Text>
                ) : (
                  <ActivityDeletedItemBadge
                    objectId={log.listingId}
                    type="listing"
                  />
                )}
              </>
            );
          };

          return {
            id: log.id,
            actor: log.actor,
            date: log.date,
            objectId: log.objectId!,
            icon: op,
            logId: log.id,
            op,
            label: getLabel(),
            detailIds:
              op === 'update' ? logs.map((x) => x.id as string) : undefined,
          };
        }
      );

      return {
        aggregate,
        list,
        count: logs.length,
      };
    },
    {
      queryKey: 'activity',
      variables: {
        teamId,
        search: filter.search?.trim(),
        dashboard,
        ownerPortalContactId,
        listingId: filter.listingId,
        actorUserId: filter.actorUserId,
        op: filter.op,
        ACTIVITY_START_DATE,
        LISTING_ACTIVITY_TABLES,
        GL,
      },
    }
  );

  return (
    <ActivityDrawer
      opened={opened}
      close={close}
      title="Listings"
      data={queryData}
      setFilter={setFilter}
      renderDetailPanel={renderDetailPanel}
      tables={LISTING_ACTIVITY_TABLES}
    >
      <ListingFilter
        listingId={filter.listingId}
        setListingId={(listingId) =>
          setFilter({
            listingId,
          })
        }
      />
    </ActivityDrawer>
  );
};

const renderDetailPanel = ({ row, isExpanded }: ActivityPanelProps) => {
  const ids = row.detailIds || [];

  const { data, isLoading, error } = useQuery(
    (q, args) => {
      const logs = q
        .auditLogs({
          where: {
            id: { _in: args.ids },
          },
          order_by: [{ createdAt: 'desc' }],
        })
        .map((log) => ({
          date: log.createdAt,
          id: log.id,
          objectId: log.objectId!,
          op: log.op as ActivityOperation,
          tableName: log.tableName as ActivityTable,
          delta: (log.deltaJson() || []) as ActivityDeltaJson,
          listingId: log.listingId!,
          listingName: getListingName(log.listing),
          ownerName: formatOwnerName(log.owner),
          ownerId: log.ownerId,
          listingCollectionId: log.listingCollectionId || null,
          listingCollectionName: log.listingCollection?.name || '',
        }));

      const listing =
        q
          .listings({
            where: {
              id: { _eq: args.objectId },
            },
          })
          .map((x) => ({
            name: getListingName(x),
          }))[0] || null;

      const deletedListingLog =
        q
          .auditLogs({
            where: { objectId: { _eq: args.objectId }, op: { _eq: 'delete' } },
          })
          .map((l) => {
            const delta = (l.deltaJson() || []) as ActivityDeltaJson;

            const title = delta.find(
              (d) => d.key === 'calculated_title'
            )?.value;

            return {
              title: title || 'No name',
            };
          })[0] || null;

      return {
        logs,
        listingName: (listing?.name || deletedListingLog?.title) as string,
      };
    },
    {
      queryKey: 'activity',
      skip: !ids?.length || !isExpanded,
      variables: {
        ids,
        objectId: row.objectId,
      },
    }
  );

  const {
    data: data2,
    isLoading: loading2,
    error: error2,
  } = useQuery(
    (q, args) => {
      const collections = q
        .listingCollections({
          where: {
            id: { _in: args.collectionIds },
          },
        })
        .map((x) => ({
          id: x.id,
          name: x.name!,
        }));

      return {
        collections,
      };
    },
    {
      skip: !data?.logs.some((x) =>
        x.delta.some((y) => y.key === 'collection_id')
      ),
      variables: {
        collectionIds:
          data?.logs
            .flatMap((x) =>
              x.delta
                .filter((y) => y.key === 'collection_id')
                .map((y) => y.value as string)
            )
            .filter(hasValue) || [],
      },
    }
  );

  const collections = data2?.collections || [];

  const rows =
    data?.logs.flatMap((log) => {
      return getListingActivityDetails(
        log,
        {
          id: log.listingId,
          name: log.listingName || data.listingName || 'No name',
        },
        {
          id: log.listingCollectionId,
          name: log.listingCollectionName,
        },
        {
          id: log.ownerId,
          name: log.ownerName,
        },
        collections
      );
    }) || [];

  return {
    loading: isLoading || loading2,
    error: error || error2,
    rows,
  };
};

const ListingFilter = ({
  listingId,
  setListingId,
}: {
  listingId: Maybe<string>;
  setListingId: (id: Maybe<string>) => void;
}) => {
  const [{ id: teamId, isGL: GL }] = useTeam();
  const [dashboard] = useDashboard();
  const { contactId: ownerPortalContactId } = useOwnerPortalContact();
  const [search, setSearch] = useState('');
  const gate = useSelectQueryGate({ value: listingId });

  const queryData = useInfiniteQuery(
    (
      q,
      { teamId, search, dashboard, ownerPortalContactId, GL },
      { limit, offset }
    ) => {
      const where: gqlV2.listing_bool_exp = whereListings({
        tenantId: teamId,
        search,
        dashboard,
        ownerPortalContactId,
        GL,
      });

      const list = q
        .listings({
          where,
          order_by: [{ calculated_title: 'asc_nulls_last' }],
          limit,
          offset,
        })
        .map<SelectItem>((res) => ({
          value: res.id,
          label: getListingName(res),
        }));

      const aggregate = q.listingAggregate({ where }).aggregate?.count() || 0;

      return {
        list,
        aggregate,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchOptions,
      queryKey: ['reservations', 'listings'],
      variables: {
        teamId,
        search: search?.trim(),
        dashboard,
        ownerPortalContactId,
        GL,
      },
    }
  );

  const { data } = useQuery(
    (q, { listingId }) => {
      if (!listingId) return null;

      const value =
        q
          .listings({ where: { id: { _eq: listingId } } })
          .map<SelectItem>((item) => ({
            label: getListingName(item),
            value: item.id,
          }))[0] || null;

      return {
        value,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchValue,
      queryKey: ['reservations', 'listings'],
      keepPreviousData: true,
      variables: {
        listingId,
      },
    }
  );

  const value = gate.hasValue ? data?.value || null : null;

  return (
    <Filter.Select
      value={value}
      setValue={(v) => setListingId(v?.value)}
      label="Listing"
      type="single"
      withinPortal
      onOpenChange={gate.onOpenChange}
      infiniteData={{ ...queryData, setSearch }}
    />
  );
};
